import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Typography, 
    Button, 
    useTheme,
    Grid
} from '@mui/material';
import { AuthDialog } from './AuthDialog';
import CodeManager from './codeowner/CodeManager';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import QrCode2RoundedIcon from '@mui/icons-material/QrCode2Rounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import FlashOnRoundedIcon from '@mui/icons-material/FlashOnRounded';

const CodeOwnerConsole = () => {
    const [showAuth, setShowAuth] = useState(false);
    const [authStep, setAuthStep] = useState('initial');
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [ownerData, setOwnerData] = useState(null);
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    // Check for existing session on mount
    useEffect(() => {
        const token = localStorage.getItem('authToken');
        const storedOwnerData = localStorage.getItem('ownerData');
        
        if (token && storedOwnerData) {
            try {
                const parsedOwnerData = JSON.parse(storedOwnerData);
                setOwnerData(parsedOwnerData);
                setIsAuthenticated(true);
            } catch (e) {
                console.error('Error parsing ownerData:', e);
            }
        }
    }, []);

    const handleAuthSuccess = (data) => {
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('ownerData', JSON.stringify({
            email: data.email,
            user: data.user,
            token: data.token
        }));
        setOwnerData(data);
        setIsAuthenticated(true);
    };

    const handleLogout = () => {
        localStorage.removeItem('authToken');
        localStorage.removeItem('ownerData');
        setOwnerData(null);
        setIsAuthenticated(false);
    };

    const handleStartAuth = () => {
        setShowAuth(true);
        setAuthStep('email');
    };

    const FeaturePill = ({ icon: Icon, title, desc }) => (
        <Box sx={{
            p: 1.5,
            borderRadius: '12px',
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.04)' : '#FFFFFF',
            border: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.25,
            height: '100%',
            boxSizing: 'border-box'
        }}>
            <Box sx={{
                p: 0.75,
                borderRadius: '8px',
                backgroundColor: isDark ? 'rgba(0, 102, 255, 0.15)' : 'rgba(0, 102, 255, 0.08)',
                color: theme.palette.primary.main,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
            }}>
                <Icon sx={{ fontSize: 18 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: theme.palette.text.primary, lineHeight: 1.2 }}>
                    {title}
                </Typography>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', mt: 0.25, lineHeight: 1.3 }}>
                    {desc}
                </Typography>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ 
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            px: { xs: 1, sm: 2 },
            py: 1
        }}>
            {/* Top Info Banner */}
            <Box sx={{ 
                flex: 'none',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start'
            }}> 
                <Typography 
                    variant="h4"
                    sx={{ 
                        color: theme.palette.text.primary,
                        fontSize: { xs: '1.25rem', sm: '1.8rem', md: '2.0rem' },
                        mb: { xs: 2, sm: 2.5 },
                        fontWeight: 800,
                        letterSpacing: '-0.02em',
                        lineHeight: 1.2
                    }}
                >
                    Create and Manage Your Code!
                </Typography>
            </Box>

            {/* Auth / CTA Container */}
            {!isAuthenticated ? (
                <Box sx={{
                    width: '100%',
                    maxWidth: '520px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch'
                }}>
                    {!showAuth ? (
                        <>
                            <Button 
                                variant="contained"
                                onClick={handleStartAuth}
                                sx={{
                                    width: '100%',
                                    height: '56px',
                                    borderRadius: '14px',
                                    background: 'linear-gradient(135deg, #0066FF 0%, #0047BA 100%)',
                                    color: '#FFFFFF',
                                    fontSize: { xs: '0.95rem', sm: '1.05rem' },
                                    fontWeight: 700,
                                    letterSpacing: '0.02em',
                                    boxShadow: '0 8px 24px -4px rgba(0, 102, 255, 0.4)',
                                    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                                    mb: 3,
                                    '&:hover': {
                                        background: 'linear-gradient(135deg, #0052CC 0%, #003D99 100%)',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 12px 30px -4px rgba(0, 102, 255, 0.5)',
                                    },
                                    '&:active': {
                                        transform: 'translateY(0)',
                                    }
                                }}
                                endIcon={<ArrowForwardRoundedIcon />}
                            >
                                CONTINUE AS A CODE OWNER
                            </Button>

                            {/* Value propositions for advertisers */}
                            <Box sx={{ mt: 1 }}>
                                <Typography 
                                    variant="caption" 
                                    sx={{ 
                                        color: theme.palette.text.secondary,
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.06em',
                                        mb: 1.5,
                                        display: 'block'
                                    }}
                                >
                                    Why advertise with MyTownCode?
                                </Typography>
                                <Grid container spacing={1.5}>
                                    <Grid item xs={12} sm={4}>
                                        <FeaturePill 
                                            icon={QrCode2RoundedIcon}
                                            title="Short & Memorable"
                                            desc="Easy to recall from billboards, radios, & cars."
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <FeaturePill 
                                            icon={FlashOnRoundedIcon}
                                            title="Instant Routing"
                                            desc="Direct navigation to your custom web destination."
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <FeaturePill 
                                            icon={InsightsRoundedIcon}
                                            title="Scan Analytics"
                                            desc="Track customer visits and campaign conversion."
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        </>
                    ) : (
                        <Box sx={{
                            backgroundColor: isDark ? '#141E30' : '#FFFFFF',
                            borderRadius: '18px',
                            border: `1px solid ${theme.palette.divider}`,
                            boxShadow: isDark ? '0 12px 32px rgba(0,0,0,0.5)' : '0 8px 24px rgba(15, 23, 42, 0.08)',
                            p: { xs: 2, sm: 3 },
                            mt: 1
                        }}>
                            <AuthDialog 
                                embedded={true}
                                onClose={() => {
                                    setShowAuth(false);
                                    setAuthStep('initial');
                                }}
                                onSuccess={handleAuthSuccess}
                                currentStep={authStep}
                                onStepChange={setAuthStep}
                            />
                        </Box>
                    )}
                </Box>
            ) : (
                ownerData && (
                    <CodeManager 
                        ownerData={ownerData}
                        onLogout={handleLogout}
                    />
                )
            )}
        </Box>
    );
};

export default CodeOwnerConsole;